/**
 * TodoFolderManager.ts
 * folder 관련 기능 모음
 *
 * 1. createFolder
 * 2. updateFolder
 * 3. deleteTodoFolder
 * 4. initDefaultTodoFolder
 */

import { getClient } from 'pages/calendar/modules/CalendarClient'
import settings from 'popups/Settings'
import { isSameDavUrl } from 'libs/dav/davPath'
import { basicColors, stringToHash } from 'utils/ui'
import { TodoFolder } from './TodoFolder'
import * as todoService from './TodoService'
import { getFolders } from './TodoFolderSync'
import { fetchTodoFolders } from './TodoSync'
import { TodoUserInfo } from './TodoService'

/**
 * Todo 폴더 생성
 * @param name 폴더 이름
 * @param color 폴더 색상
 * @param completeCallback 콜백
 */
export const createFolder = (
  isDefault: boolean,
  name: string,
  color: string,
  completeCallback = (_success: boolean, _folderUrl?: string) => {},
) => {
  console.log('[createFolder] Creating todo folder:', name, color)
  const client = getClient()

  let xml = '<?xml version="1.0" encoding="utf-8" ?>'
  xml +=
    '<D:mkcol xmlns:D="DAV:" xmlns:CAL="urn:ietf:params:xml:ns:caldav" xmlns:CARD="urn:ietf:params:xml:ns:carddav" xmlns:n0="http://apple.com/ns/ical/">'
  xml += '<D:set>'
  xml += '<D:prop>'
  xml += '<D:resourcetype>'
  xml += '<D:collection />'
  xml += '<CAL:calendar />'
  xml += '</D:resourcetype>'
  xml += '<D:displayname>' + name + '</D:displayname>'
  xml += '<n0:calendar-color>' + color + '</n0:calendar-color>'
  xml += '<CAL:supported-calendar-component-set>'
  xml += '<CAL:comp name="VTODO" />' // VTODO 지원 추가
  xml += '</CAL:supported-calendar-component-set>'
  xml += '</D:prop>'
  xml += '</D:set>'
  xml += '</D:mkcol>'

  const newId = isDefault ? 'default_tasks' : crypto.randomUUID()
  const newUrl = client.account.homeUrl + newId + '/'

  client
    .davRequest({
      url: newUrl,
      convertIncoming: false,
      init: {
        method: 'MKCOL',
        body: xml,
        headers: {
          'Content-Type': 'application/xml; charset="utf-8"',
          Depth: '0',
        },
      },
    })
    .then(async (response) => {
      console.log('[createFolder] create todo folder success %o', response)

      try {
        // 1. 강제로 폴더 목록 새로고침 (isInit=true로 강제 UI 업데이트)
        fetchTodoFolders(true, (refreshSuccess) => {
          if (refreshSuccess) {
            console.log('[createFolder] Todo folder list refreshed after creation')
          } else {
            console.warn('[createFolder] Failed to refresh folder list after creation')
          }
          completeCallback(true, newUrl)
        })
      } catch (error) {
        console.error('[createFolder] Error during post-creation processing:', error)
        // 에러가 있어도 폴더 목록은 새로고침 시도
        fetchTodoFolders(true, () => {
          completeCallback(true)
        })
      }
    })
    .catch((error) => {
      console.log('[createFolder] create todo folder failed : %o', error)
      completeCallback(false)
    })
}

/**
 * Todo 폴더 업데이트
 * @param folderUrl 폴더 URL
 * @param name 폴더 이름
 * @param color 폴더 색상
 * @param completeCallback 콜백
 */
export const updateFolder = (
  folderUrl: string,
  name: string,
  color: string,
  completeCallback = (_success: boolean) => {},
) => {
  const client = getClient()

  // CalDAV PROPPATCH 요청으로 서버 업데이트
  let xml = '<?xml version="1.0" encoding="utf-8" ?>'
  xml += '<D:propertyupdate xmlns:D="DAV:" xmlns:A="http://apple.com/ns/ical/">'
  xml += '<D:set>'
  xml += '<D:prop>'
  xml += '<D:displayname>' + name + '</D:displayname>'
  xml += '<A:calendar-color>' + color + '</A:calendar-color>'
  xml += '</D:prop>'
  xml += '</D:set></D:propertyupdate>'

  client
    .davRequest({
      url: folderUrl,
      convertIncoming: false,
      init: {
        method: 'PROPPATCH',
        body: xml,
      },
    })
    .then(async (response) => {
      console.log('[updateFolder] update todo folder success %o', response)

      try {
        // 1. 강제로 폴더 목록 새로고침 (isInit=true로 강제 UI 업데이트)
        fetchTodoFolders(true, (refreshSuccess) => {
          if (refreshSuccess) {
            console.log('[updateFolder] Todo folder list refreshed after update')
          } else {
            console.warn('[updateFolder] Failed to refresh folder list after update')
          }
          completeCallback(true)
        })
      } catch (error) {
        console.error('[updateFolder] Error during post-update processing:', error)
        // 에러가 있어도 폴더 목록은 새로고침 시도
        fetchTodoFolders(true, () => {
          completeCallback(true)
        })
      }
    })
    .catch((error) => {
      console.log('[updateFolder] todo folder failed : %o', error)
      fetchTodoFolders(false)
      completeCallback(false)
    })
}

/**
 * Todo 폴더 삭제
 * @param folderUrl 폴더 URL
 * @param completeCallback 콜백
 */
export const deleteTodoFolder = (
  folderUrl: string,
  completeCallback = (_success: boolean) => {},
) => {
  const client = getClient()

  // 로컬 상태에서 폴더 먼저 제거 (낙관적 업데이트)
  const folders = getFolders()
  let oldFolder: TodoFolder | null = null
  let oldFolderIndex = -1

  if (folders.length > 0) {
    for (let index = 0; index < folders.length; index++) {
      const f = folders[index]
      if (f.url === folderUrl) {
        oldFolder = f
        oldFolderIndex = index
        break
      }
    }
  }

  // 해당 폴더의 todo 항목들도 제거
  todoService.removeTodoItems(folderUrl)

  // CalDAV DELETE 요청으로 서버에서 폴더 삭제
  client
    .davRequest({
      url: folderUrl,
      convertIncoming: false,
      init: {
        method: 'DELETE',
        body: '',
      },
    })
    .then(async (response) => {
      console.log('delete todo folder success %o', response)

      // DELETE 성공 후 최신 settings를 재조회하여 sync 설정 제거.
      // 요청 중 다른 경로(서버 sync, 폴더 생성 등)에서 todoSyncTargets가
      // 변경되었을 수 있으므로, 캡처된 값 대신 최신 상태 기반으로 필터링한다.
      const latestTodoSettings = settings.getValue('todo') || { todoSyncTargets: [] }
      const latestSyncTargets = Array.isArray(latestTodoSettings.todoSyncTargets)
        ? latestTodoSettings.todoSyncTargets
        : []
      const newTodo = {
        ...latestTodoSettings,
        todoSyncTargets: latestSyncTargets.filter((url: string) => !isSameDavUrl(url, folderUrl)),
      }
      settings
        .setValue('todo', newTodo)
        .then(() => {
          settings.getStore().dispatch({
            type: 'settings/UPLOAD_SETTINGS',
            payload: { todo: newTodo },
          })
        })
        .catch((err) => console.error('Failed to sync todo settings:', err))

      try {
        // DB에서 폴더 삭제 (폴더 자체 제거)
        const { isTodoDBEnabled, isTodoDBReady, getTodoDB } = await import('./todoDB')
        if (isTodoDBEnabled() && isTodoDBReady()) {
          try {
            const todoDB = getTodoDB()
            await todoDB.deleteFolder(folderUrl)
            console.log(`[deleteTodoFolder] Folder deleted from DB: ${folderUrl}`)
          } catch (dbError) {
            console.error(
              `[deleteTodoFolder] Failed to delete folder from DB: ${folderUrl}`,
              dbError,
            )
          }
        }

        console.log('[deleteTodoFolder] Folder deleted successfully')

        // 1. 강제로 폴더 목록 새로고침 (isInit=true로 강제 UI 업데이트)
        fetchTodoFolders(true, (refreshSuccess) => {
          if (refreshSuccess) {
            console.log('[deleteTodoFolder] Todo folder list refreshed after deletion')
          } else {
            console.warn('[deleteTodoFolder] Failed to refresh folder list after deletion')
          }
          completeCallback(true)
        })
      } catch (error) {
        console.error('[deleteTodoFolder] Error during post-deletion processing:', error)
        // 에러가 있어도 폴더 목록은 새로고침 시도
        fetchTodoFolders(true, () => {
          completeCallback(true)
        })
      }
    })
    .catch((error) => {
      console.log('[deleteTodoFolder] delete todo folder failed : %o', error)
      if (oldFolder && oldFolderIndex !== -1) {
        // 폴더 목록 롤백 상태 UI 반영
        fetchTodoFolders(true)
      }
      completeCallback(false)
    })
}

/**
 * 기본 Todo 폴더 초기화
 * @param info 사용자 정보
 */
export const initDefaultTodoFolder = (info: TodoUserInfo) => {
  // 'Tasks' 폴더 추가. defulat_tasks 란 id 를 가진 folder, VTodo support 만 설정.
  const hashString = `${info.email}/${info.name}/Tasks`
  const color = basicColors[Math.abs(stringToHash(hashString)) % basicColors.length]

  createFolder(true, 'Tasks', color, (success) => {
    if (success) {
      fetchTodoFolders(true, (success) => {
        if (success) {
          console.log('initDefaultTodoFolder success')
        }
      })
    }
  })
}
